import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Card, CardHeader, CardContent, Divider, Typography, Stack, Box, IconButton, Tooltip } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  customerCardPanelHeaderSx,
  customerCardPanelLabelSx,
  customerCardPanelTitleTypographyProps,
  customerCardPanelValueSx,
} from "./customerCardPanelStyles";

const ContactLineItem = ({ label, value }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "220px minmax(0, 1fr)",
      columnGap: 1,
      alignItems: "start",
    }}
  >
    <Typography variant="body2" sx={customerCardPanelLabelSx}>
      {label}:
    </Typography>
    <Typography variant="body2" sx={customerCardPanelValueSx}>
      {value || "-"}
    </Typography>
  </Box>
);

const CustomerPrimaryContactsPanel = ({ customerId, onOpenContactsTab }) => {
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    if (!customerId) {
      setContacts([]);
      return;
    }

    const fetchContacts = async () => {
      const response = await axios.get(`/api/v1/contactsByEntityId/${customerId}`);
      setContacts(response.data || []);
    };

    fetchContacts().catch((error) => console.error("Error loading contacts:", error));
  }, [customerId]);

  const primaryContacts = useMemo(() => contacts.filter((item) => item.isPrimaryContact), [contacts]);

  return (
    <Card>
      <CardHeader
        title="Contacts"
        titleTypographyProps={customerCardPanelTitleTypographyProps}
        sx={customerCardPanelHeaderSx}
        action={
          <Tooltip title="Open Contacts tab">
            <IconButton aria-label="open contacts tab" onClick={onOpenContactsTab} size="small">
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        }
      />
      <Divider />
      <CardContent sx={{ px: 2, pt: 1, pb: 1.25, "&:last-child": { pb: 1.25 } }}>
        <Stack spacing={1}>
          {primaryContacts.length === 0 && (
            <Typography variant="body2" sx={customerCardPanelValueSx} color="text.secondary">
              No primary contacts selected yet.
            </Typography>
          )}
          {primaryContacts.map((contact) => (
            <Box
              key={contact.contactId}
              className="text-on-light-surface"
              sx={{
                border: "1px solid",
                borderColor: "grey.300",
                borderRadius: 2,
                backgroundColor: "grey.50",
                color: "grey.900",
                px: 1.5,
                py: 1,
              }}
            >
              <Stack spacing={0.5}>
                <ContactLineItem label="Name" value={contact.contactName} />
                <ContactLineItem label="Job Title" value={contact.contactJobTitle || "No job title"} />
                <ContactLineItem label="Direct Number" value={contact.contactDirectNumber} />
              </Stack>
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default CustomerPrimaryContactsPanel;
